/**
 * TopBar Component
 *
 * Top navigation bar for authenticated pages, displaying:
 * - Logo/brand on the left
 * - Organization name (with fallback to default title)
 * - User menu on the right with a profile link and sign-out option
 *
 * Named "TopBar" to avoid confusion with MUI's AppBar component.
 *
 * @module components/TopBar
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Divider,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AddIcon from '@mui/icons-material/Add';
import { useAuthStore } from '../stores/useAuthStore';
import { OrgPicker } from './OrgPicker';
import { CreateTenantDialog } from './CreateTenantDialog';
import { ROUTES } from '../routes/constants';

// =============================================================================
// Constants
// =============================================================================

/**
 * Brand name displayed in the AppBar.
 */
const BRAND_NAME = 'ModernPath';

// =============================================================================
// TopBar Component
// =============================================================================

/**
 * TopBar - Navigation bar component for authenticated pages.
 *
 * Features:
 * - Displays ModernPath logo/brand on the left
 * - Shows organization name from auth store (falls back to "Tenant Management")
 * - User menu on the right with account icon
 * - Dropdown menu showing user email, a profile link, and a sign out option
 *
 * The component is self-contained and reads directly from the auth store.
 *
 * @example
 * ```tsx
 * function UsersPage() {
 *   return (
 *     <>
 *       <TopBar />
 *       <main>Page content</main>
 *     </>
 *   );
 * }
 * ```
 */
export function TopBar() {
  // Auth store state
  const orgName = useAuthStore((state) => state.orgName);
  const userEmail = useAuthStore((state) => state.userEmail);
  const logout = useAuthStore((state) => state.logout);
  // Staff admins choose which org to manage via the picker; it occupies the
  // same slot the static org name otherwise would.
  const isStaffAdmin = useAuthStore((state) => state.isStaffAdmin);
  // Instance-level tenant-management authority gates the Create-tenant affordance.
  const canManageTenants = useAuthStore((state) => state.canManageTenants);

  // Router navigation
  const navigate = useNavigate();

  // Menu anchor state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(anchorEl);

  // Create-tenant dialog + success snackbar (self-contained here, next to the
  // picker where the affordance lives).
  const [createOpen, setCreateOpen] = useState(false);
  const [createdMessage, setCreatedMessage] = useState<string | null>(null);

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  /**
   * Opens the user menu.
   */
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Closes the user menu.
   */
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  /**
   * Navigates to the user's profile page.
   */
  const handleProfile = () => {
    handleMenuClose();
    navigate(ROUTES.PROFILE);
  };

  /**
   * Navigates back to the portal's front page (the users page). The brand is
   * the conventional "logo → home" backlink, letting authenticated users return
   * from any page (e.g. /profile) without relying on the browser back button.
   */
  const handleBrandClick = () => {
    navigate(ROUTES.USERS);
  };

  /**
   * Handles sign out action.
   * Calls the logout action with the landing page URL as returnTo.
   */
  const handleSignOut = () => {
    handleMenuClose();
    // Construct the full landing page URL for returnTo
    const returnTo = `${window.location.origin}${ROUTES.LANDING}`;
    logout(returnTo);
  };

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <>
    <AppBar position="static">
      <Toolbar>
        {/* Left Section: Logo (clickable → portal home) and Organization Name.
            Rendered as a real <button> so it's keyboard-focusable and works
            without a Router in tests (navigation goes through useNavigate). */}
        <Typography
          variant="h6"
          component="button"
          type="button"
          onClick={handleBrandClick}
          aria-label="Go to portal home"
          sx={{
            fontWeight: 'bold',
            cursor: 'pointer',
            // Strip native button chrome so it still reads as the brand text.
            background: 'none',
            border: 'none',
            p: 0,
            color: 'inherit',
            '&:hover': { opacity: 0.85 },
            '&:focus-visible': {
              outline: '2px solid rgba(255, 255, 255, 0.7)',
              outlineOffset: 2,
              borderRadius: 1,
            },
          }}
          data-testid="topbar-brand"
        >
          {BRAND_NAME}
        </Typography>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ mx: 2, borderColor: 'rgba(255, 255, 255, 0.3)' }}
        />

        {/* Org slot: a staff admin gets the OrgPicker here (it selects which
            org's members they manage); everyone else sees the static org name.
            The picker still drives `targetOrgId` via the users store — this is
            purely where it renders. */}
        {isStaffAdmin ? (
          <OrgPicker compact />
        ) : (
          <Typography
            variant="subtitle1"
            component="div"
            data-testid="topbar-org-name"
          >
            {orgName}
          </Typography>
        )}

        {/* Create-tenant action, next to the picker. Only for staff who hold
            instance-level tenant-management authority. */}
        {canManageTenants && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              ml: 2,
              color: 'inherit',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': { borderColor: 'inherit' },
            }}
            data-testid="topbar-create-tenant"
          >
            Create tenant
          </Button>
        )}

        {/* Spacer to push user menu to the right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right Section: User Menu */}
        <IconButton
          size="large"
          edge="end"
          aria-label="user account menu"
          aria-controls={isMenuOpen ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ? 'true' : undefined}
          onClick={handleMenuOpen}
          color="inherit"
          data-testid="topbar-user-menu-button"
        >
          <AccountCircleIcon />
        </IconButton>

        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            list: { 'aria-labelledby': 'user-menu-button' },
          }}
          data-testid="topbar-user-menu"
        >
          {/* User email - informational, not clickable */}
          <MenuItem
            disabled
            sx={{
              '&.Mui-disabled': {
                opacity: 1,
                color: 'text.primary',
              },
            }}
            data-testid="topbar-user-email"
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {userEmail}
            </Typography>
          </MenuItem>

          <Divider />

          {/* Profile link */}
          <MenuItem onClick={handleProfile} data-testid="topbar-profile">
            Profile
          </MenuItem>

          {/* Sign out option */}
          <MenuItem onClick={handleSignOut} data-testid="topbar-sign-out">
            Sign out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>

      {/* Create-tenant modal + success snackbar */}
      <CreateTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={(message) => setCreatedMessage(message)}
      />
      <Snackbar
        open={Boolean(createdMessage)}
        autoHideDuration={5000}
        onClose={() => setCreatedMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="topbar-create-tenant-snackbar"
      >
        <Alert
          onClose={() => setCreatedMessage(null)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {createdMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
